import {
  ChangeDetectionStrategy,
  Component,
  Inject,
  OnDestroy,
} from '@angular/core';
import {
  FormBuilder,
  FormGroup,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import {
  MAT_DIALOG_DATA,
  MatDialogModule,
  MatDialogRef,
} from '@angular/material/dialog';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatSlideToggleModule } from '@angular/material/slide-toggle';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { Subject } from 'rxjs';
import { finalize, takeUntil } from 'rxjs/operators';
import { OAuthClient } from '../../../dto/oauth/oauth-client.dto';
import { OAuthClientsService } from '../../../services/oauth/oauth-clients.service';

export interface DunaOAuthClientsEditDialogData {
  client: OAuthClient;
}

@Component({
  selector: 'app-duna-oauth-clients-edit-dialog',
  standalone: true,
  imports: [
    ReactiveFormsModule,
    MatDialogModule,
    MatButtonModule,
    MatFormFieldModule,
    MatInputModule,
    MatSlideToggleModule,
    MatProgressSpinnerModule,
  ],
  changeDetection: ChangeDetectionStrategy.Eager,
  template: `
    <h2 mat-dialog-title>Rediger OAuth-klient</h2>
    <mat-dialog-content>
      <p class="client-id-hint">Client ID: <strong>{{ data.client.client_id }}</strong></p>
      <form [formGroup]="form" class="form">
        <mat-form-field appearance="outline" class="full-width">
          <mat-label>Navn</mat-label>
          <input matInput formControlName="name" autocomplete="off" />
        </mat-form-field>

        <mat-slide-toggle formControlName="enabled">Aktiv</mat-slide-toggle>

        <mat-form-field appearance="outline" class="full-width">
          <mat-label>Redirect URIs</mat-label>
          <textarea matInput formControlName="redirect_uris" rows="4"></textarea>
        </mat-form-field>

        @if (errorMessage) {
        <p class="error">{{ errorMessage }}</p>
        }
      </form>
    </mat-dialog-content>
    <mat-dialog-actions align="end">
      <button mat-button type="button" [disabled]="saving" (click)="cancel()">
        Annuller
      </button>
      <button
        mat-raised-button
        color="primary"
        type="button"
        [disabled]="saving || form.invalid"
        (click)="submit()"
      >
        @if (saving) {
        <mat-spinner diameter="20"></mat-spinner>
        } @else {
        Gem
        }
      </button>
    </mat-dialog-actions>
  `,
  styles: [
    `
      .form {
        display: flex;
        flex-direction: column;
        gap: 12px;
        min-width: min(440px, 90vw);
      }
      .full-width {
        width: 100%;
      }
      .client-id-hint {
        margin: 0 0 8px;
      }
      .error {
        color: var(--mat-sys-error, #b00020);
      }
    `,
  ],
})
export class DunaOAuthClientsEditDialogComponent implements OnDestroy {
  form: FormGroup;
  saving = false;
  errorMessage: string | null = null;

  private destroy$ = new Subject<void>();

  constructor(
    @Inject(MAT_DIALOG_DATA) public data: DunaOAuthClientsEditDialogData,
    private fb: FormBuilder,
    private dialogRef: MatDialogRef<
      DunaOAuthClientsEditDialogComponent,
      OAuthClient | undefined
    >,
    private oauthClients: OAuthClientsService
  ) {
    this.form = this.fb.group({
      name: [data.client.name, Validators.required],
      enabled: [data.client.enabled],
      redirect_uris: [
        (data.client.redirect_uris || []).join('\n'),
        Validators.required,
      ],
    });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  cancel(): void {
    this.dialogRef.close(undefined);
  }

  submit(): void {
    if (this.form.invalid || this.saving) {
      this.form.markAllAsTouched();
      return;
    }
    const redirectUris = this.parseRedirectUris(
      this.form.value.redirect_uris as string
    );
    if (redirectUris.length === 0) {
      this.errorMessage = 'Angiv mindst én redirect URI.';
      return;
    }

    this.saving = true;
    this.errorMessage = null;
    this.oauthClients
      .update(this.data.client.id, {
        name: (this.form.value.name as string).trim(),
        enabled: !!this.form.value.enabled,
        redirect_uris: redirectUris,
      })
      .pipe(
        takeUntil(this.destroy$),
        finalize(() => {
          this.saving = false;
        })
      )
      .subscribe({
        next: (client) => this.dialogRef.close(client),
        error: (err) => {
          this.errorMessage = this.formatError(err, 'Kunne ikke gemme ændringer.');
        },
      });
  }

  private parseRedirectUris(text: string): string[] {
    return text
      .split(/\r?\n/)
      .map((line) => line.trim())
      .filter((line) => line.length > 0);
  }

  private formatError(error: unknown, fallback: string): string {
    const httpErr = error as { error?: unknown };
    if (httpErr?.error && typeof httpErr.error === 'object') {
      const msg = (httpErr.error as { message?: string }).message;
      if (msg) return msg;
    }
    if (typeof httpErr?.error === 'string') {
      try {
        const parsed = JSON.parse(httpErr.error);
        if (parsed?.message) return String(parsed.message);
      } catch {
        return httpErr.error;
      }
    }
    return fallback;
  }
}
